/**
 * 买家端在线咨询会话 Hook：从官网 useCustomerChat 移植。
 * 凭据持久化（audience 维度隔离）、seq 游标增量拉取、3s 轮询（仅抽屉打开且页面可见时）、
 * 发送幂等（requestId）、转人工/留资/新会话；错误收敛为 'error' | 'expired'。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { chatRequest, buyerChatRequest, chatRequestId, savedChat, saveChat, mergeVisibleMessages, type Lang, type Audience, type LeadInput, type ChatCredential, type ChatMessage, type ChatSession, type ChatView } from '@/lib/customerChat';

export function useCustomerChat(open: boolean, lang: Lang, audience: Audience = 'GENERAL') {
  const credential = useRef<ChatCredential | null>(savedChat(audience));
  const starting = useRef<Promise<ChatCredential> | null>(null);
  const cursor = useRef(0);
  const refreshing = useRef(false);
  const writing = useRef(false);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'error' | 'expired' | null>(null);
  const errorOrigin = useRef<'read' | 'write' | null>(null);
  const pending = useRef<{ requestId: string; content: string; locale: Lang } | null>(null);
  const pendingLead = useRef<{ requestId: string; payload: string } | null>(null);
  const handleError = useCallback((e: unknown, origin: 'read' | 'write') => {
    errorOrigin.current = origin;
    setError((e as { sessionExpired?: boolean }).sessionExpired ? 'expired' : 'error');
  }, []);
  const refresh = useCallback(async () => {
    const auth = credential.current;
    if (!auth || refreshing.current) return;
    refreshing.current = true;
    try {
      let more = true;
      while (more && credential.current === auth) {
        const data = await chatRequest<ChatView>(auth, `?after=${cursor.current}`);
        if (credential.current !== auth) return;
        if (errorOrigin.current === 'read') { errorOrigin.current = null; setError(null); }
        setSession(data.session);
        if (data.messages.length) {
          cursor.current = data.messages[data.messages.length - 1].seq;
        }
        setMessages(prev => mergeVisibleMessages(prev, data));
        more = data.hasMore;
      }
    } catch (e) { if (credential.current === auth) handleError(e, 'read'); }
    finally { refreshing.current = false; }
  }, [handleError]);
  useEffect(() => {
    if (!open) return;
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 3000);
    return () => window.clearInterval(timer);
  }, [open, refresh]);
  const ensure = async () => {
    if (credential.current) return credential.current;
    if (!starting.current) starting.current = chatRequest<ChatCredential>(null, '', { locale: lang, source: window.location.pathname, audience })
      .then(auth => { credential.current = auth; saveChat(auth, audience); return auth; }).finally(() => { starting.current = null; });
    return starting.current;
  };
  const write = async (path: string, body: unknown) => {
    if (writing.current) return false;
    writing.current = true; setBusy(true); errorOrigin.current = null; setError(null);
    try { await chatRequest(await ensure(), path, body); await refresh(); return true; }
    catch (e) { handleError(e, 'write'); return false; }
    finally { writing.current = false; setBusy(false); }
  };
  const send = async (content: string) => {
    if (!pending.current || pending.current.content !== content) {
      pending.current = { requestId: chatRequestId(), content, locale: lang };
    }
    const ok = await write('/messages', pending.current);
    if (ok) pending.current = null;
    return ok;
  };
  const restart = () => {
    credential.current = null; saveChat(null, audience); cursor.current = 0; pending.current = null; pendingLead.current = null;
    setSession(null); setMessages([]); errorOrigin.current = null; setError(null);
  };

  /** 登录态：认领本机访客会话（幂等，失败静默——不阻断聊天） */
  const claimGuest = useCallback(async (bearer: string): Promise<boolean> => {
    const auth = credential.current;
    if (!auth) return false;
    try {
      await buyerChatRequest(bearer, '/claim', { id: auth.id, token: auth.token });
      void refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  /** 登录态：创建归属买家的新会话并切换为当前会话（凭据仍走 public 通道续聊） */
  const newSessionSignedIn = useCallback(async (bearer: string): Promise<boolean> => {
    try {
      const created = await buyerChatRequest<ChatCredential>(bearer, '', { locale: lang, source: window.location.pathname });
      saveChat(created, audience);
      credential.current = created; cursor.current = 0; pending.current = null; pendingLead.current = null;
      setSession(null); setMessages([]); errorOrigin.current = null; setError(null);
      void refresh();
      return true;
    } catch { return false; }
  }, [audience, lang, refresh]);

  /** 登录态：我的会话列表（最近 30 条；失败返回空数组不阻断 UI） */
  const listMine = useCallback(async (bearer: string): Promise<ChatSession[]> => {
    try { return await buyerChatRequest<ChatSession[]>(bearer, ''); }
    catch { return []; }
  }, []);

  const submitLead = async (data: LeadInput) => {
    const payload = JSON.stringify(data);
    if (!pendingLead.current || pendingLead.current.payload !== payload) pendingLead.current = { payload, requestId: chatRequestId() };
    const ok = await write('/lead', { ...data, requestId: pendingLead.current.requestId });
    if (ok) pendingLead.current = null;
    return ok;
  };
  return { session, messages, busy, error, send, restart, claimGuest, newSessionSignedIn, listMine,
    submitLead,
    human: () => write('/human', {}), profile: (name: string, contact: string) => write('/profile', { name, contact }) };
}
